import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  FaUserCircle, FaUserCog, FaUnlink, FaEdit, FaTrash, FaExclamationTriangle,
} from 'react-icons/fa';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

function UserItem({
  user, admins, onAssignAdmin, onUnassignAdmin, onDeleteUser,
}) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    await onDeleteUser(user.id);
    setIsDeleting(false);
    setShowDeleteModal(false);
  };

  const handleAdminChange = (e) => {
    const adminId = e.target.value;
    if (adminId) {
      onAssignAdmin(user.id, adminId);
    }
  };

  return (
    <>
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 rounded-full p-2">
                <FaExclamationTriangle className="text-red-500 text-xl" />
              </div>
              <h2 className="text-lg font-semibold text-gray-800">Eliminar usuario</h2>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              ¿Estás seguro de que deseas eliminar al usuario
              {' '}
              <span className="font-semibold text-gray-800">{user.username}</span>
              ?
            </p>
            <p className="text-xs text-red-500 mb-6">Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
                className={`
                  flex-1 px-4 py-2 rounded-lg text-sm font-medium
                  text-gray-700 bg-gray-100 hover:bg-gray-200
                  transition-colors duration-200 disabled:opacity-50
                `}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className={`
                  flex-1 px-4 py-2 rounded-lg text-sm font-medium
                  text-white bg-red-600 hover:bg-red-700
                  transition-colors duration-200 disabled:opacity-50
                `}
              >
                {isDeleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className={`
          bg-white rounded-lg shadow-md hover:shadow-xl
          transition-all duration-300 p-4 flex flex-col h-full
          transform hover:-translate-y-1 overflow-hidden
        `}
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-blue-100 rounded-full p-2">
            <FaUserCircle className="text-blue-500 text-xl" />
          </div>
          <div className="min-w-0 flex-1">
            <Tippy
              content={user.username}
              placement="top"
              maxWidth={300}
              popperOptions={{
                strategy: 'fixed',
              }}
            >
              <h3 className="text-base font-semibold text-gray-800 line-clamp-1">{user.username}</h3>
            </Tippy>
            <p className="text-xs text-gray-500">Usuario</p>
          </div>
        </div>

        <div className="flex-1 space-y-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <FaUserCog className="text-gray-400 text-sm" />
            </div>
            <select
              onChange={handleAdminChange}
              defaultValue=""
              className={`
                w-full pl-8 pr-2 py-1.5 border border-gray-300 rounded-lg text-sm
                text-gray-700 bg-white focus:outline-none focus:ring-2
                focus:ring-blue-500 focus:border-transparent transition-all
                duration-200
              `}
            >
              <option value="" disabled>Asignar Administrador</option>
              {admins.map((admin) => (
                <option key={admin.id} value={admin.id}>{admin.username}</option>
              ))}
            </select>
          </div>

          {user.admin_info && user.admin_info.length > 0 && (
            <div className="space-y-2">
              {user.admin_info.map((admin) => (
                <div
                  key={admin.admin_id}
                  className="flex items-center justify-between bg-gray-50 rounded-lg p-2"
                >
                  <div className="flex items-center space-x-2">
                    <FaUserCog className="text-gray-500 text-sm" />
                    <div>
                      <p className="text-xs text-gray-600">Administrado por:</p>
                      <p className="text-sm font-medium text-gray-800 line-clamp-1">
                        {admin.admin_username}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => onUnassignAdmin(user.id, admin.admin_id)}
                    type="button"
                    className={`
                      inline-flex items-center space-x-1 px-2 py-1
                      text-xs font-medium text-red-600 hover:text-red-800
                      bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-200
                    `}
                  >
                    <FaUnlink className="text-xs" />
                    <span>Desvincular</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2">
          <button
            type="button"
            onClick={() => navigate(`/edit-user/${user.id}`)}
            className={`
              flex-1 inline-flex items-center justify-center gap-2
              px-3 py-1.5 rounded-lg text-sm font-medium
              text-blue-600 bg-blue-50 hover:bg-blue-100
              transition-colors duration-200
            `}
          >
            <FaEdit className="text-sm" />
            Editar
          </button>
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className={`
              flex-1 inline-flex items-center justify-center gap-2
              px-3 py-1.5 rounded-lg text-sm font-medium
              text-red-600 bg-red-50 hover:bg-red-100
              transition-colors duration-200
            `}
          >
            <FaTrash className="text-sm" />
            Eliminar
          </button>
        </div>
      </div>
    </>
  );
}

UserItem.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    username: PropTypes.string.isRequired,
    admin_info: PropTypes.arrayOf(PropTypes.shape({
      admin_id: PropTypes.number.isRequired,
      admin_username: PropTypes.string.isRequired,
    })),
  }).isRequired,
  admins: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      username: PropTypes.string.isRequired,
    }),
  ).isRequired,
  onAssignAdmin: PropTypes.func.isRequired,
  onUnassignAdmin: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
};

export default UserItem;
